import React from 'react'
import styled from 'styled-components'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div/Div'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  borderRadius,
  borderRadiusSizes,
  colorConstants,
  dropShadows,
  dropShadowVariations,
  iconSizes,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { appResource } from '@jarvis.oddle/core/env'
import { useFormContext } from '@jarvis.oddle/core/components/Form'
import { pixelise, setAlpha } from '@jarvis.oddle/core/tools/style'
import { isObjectEmpty } from 'src/utils/object'
import {
  modalFooterHeight,
  modalHeaderHeight
} from '@jarvis.oddle/core/components/Modal'
import { SmallText } from '@jarvis.oddle/core/components/Typo'
import Carousel from '@jarvis.oddle/core/components/Carousel'
import IconArrow from '@jarvis.oddle/core/components/Icons/IconArrow'
import MapService, { mapServices } from 'src/containers/MapService'
import { isEmpty } from 'src/utils/string'
import { origin } from 'config/universal'
import StoreInformationCard from './StoreInformationCard'
import StoreInformationSummary from './StoreInformationSummary'
import { sortAvailableStores } from './helper'
import { ResponsiveContext } from 'src/decorators/Responsive'

const HEADER_HEIGHT = pixelise(modalHeaderHeight)
const FOOTER_HEIGHT = pixelise(modalFooterHeight)
const ARROW_CONTAINER_SIZE = '1.25rem'

const Container = styled(Flex)`
  background-color: ${(props) => props.theme.colorBackgroundColor};
  width: 100%;
  height: 100%;
`

const MapContainer = styled(Flex)`
  width: 100%;
  height: 100%;

  .marker-label {
    position: absolute;
    left: ${margin[marginSizes.medium]};
    top: 50%;
    transform: translateY(-50%);
    width: 100px;
    white-space: pre-line;
    word-wrap: break-word;
    text-align: left;
    font-weight: bold;
    font-size: 2em;
    color: ${colorConstants.black};
    padding: ${padding[paddingSizes.extraSmall]};
    border-radius: ${borderRadius[borderRadiusSizes.small]};

    /* https://css3gen.com/text-shadow/ */
    text-shadow: 3px 0 0 ${colorConstants.white};
  }
`

const ChipContainer = styled(Flex)`
  position: absolute;
  top: ${HEADER_HEIGHT};
  left: 0;
  right: 0;
  padding: ${padding[paddingSizes.medium]};
  flex-direction: row;
  justify-content: center;
  align-items: center;
  pointer-events: none;
`

const Chip = styled(Flex)`
  border-radius: ${borderRadius[borderRadiusSizes.large]};
  padding: ${padding[paddingSizes.extraSmall]} ${padding[paddingSizes.extraLarge]};
  background-color: ${(props) => props.color};
  flex-direction: row;
  justify-content: center;
  align-items: center;
  opacity: 0.8;
`

const ArrowContainer = styled(Flex)`
  position: absolute;
  top: 50%;
  ${(props) => (props.left ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);
  height: ${ARROW_CONTAINER_SIZE};
  width: ${ARROW_CONTAINER_SIZE};
  z-index: 1;
  color: ${(props) =>
    props.disabled
      ? setAlpha(props.theme.buttonColor, 0.5)
      : props.theme.buttonColor};
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const BottomSheetContainer = styled(Div)`
  position: absolute;
  bottom: ${FOOTER_HEIGHT};
  left: 0;
  right: 0;
  max-width: 100vw;
  background-color: ${(props) => props.theme.colorContainerColor};
  box-shadow: ${dropShadows[dropShadowVariations.heavy]};
  padding: ${padding[paddingSizes.extraSmall]};
`

const PromotionCardWrapper = styled(Div)`
  width: 100%;
  padding: ${padding[paddingSizes.small]}
    calc(${ARROW_CONTAINER_SIZE} + ${padding[paddingSizes.small]});
  display: inline-block;
  vertical-align: bottom;
  cursor: ${(props) => (props.isExpanded ? 'all' : 'pointer')};

  &:focus {
    outline: 0;
  }
`

const NextArrow = (props) => (
  <ArrowContainer left={false} role='button' {...props}>
    <IconArrow.Right fill='currentColor' iconSize={iconSizes.medium} />
  </ArrowContainer>
)

const PrevArrow = (props) => (
  <ArrowContainer left role='button' {...props}>
    <IconArrow.Left fill='currentColor' iconSize={iconSizes.medium} />
  </ArrowContainer>
)

const StoreViewer = ({
  availableStores,
  mapServiceProps,
  onViewStoreClick
}: {
  availableStores: Oms.V4.Store[]
  mapServiceProps: {
    getMapProps
    mapUtils: {
      setMapCenter: (params) => void
      getZoomLevel: () => number
    }
    MapComponent
  }
  onViewStoreClick: () => void
}): JSX.Element => {
  const { t } = React.useContext(TranslationContext)
  const { desktop } = React.useContext(ResponsiveContext)
  const { values, setFieldValue } = useFormContext()
  const carouselRef = React.useRef(
    {} as {
      slickGoTo: (index) => void
    }
  )
  const [showInfo, setShowInfo] = React.useState(true)

  const { selectedStoreId, userAddress } = values || {}

  const sortedAvailableStores = React.useMemo(() => {
    return sortAvailableStores({ availableStores, userAddress })
  }, [availableStores, userAddress])

  const selectedStore = React.useMemo(() => {
    return !isEmpty(selectedStoreId)
      ? (sortedAvailableStores || []).find(
          (store) => store.id === selectedStoreId
        )
      : (sortedAvailableStores || [])[0]
  }, [selectedStoreId, sortedAvailableStores])

  const { getMapProps, mapUtils, MapComponent } = mapServiceProps || {}

  const mapProps = getMapProps()

  const { mapServices } = mapProps || {}
  const numOfStores = (sortedAvailableStores || []).length
  const showArrows = showInfo && numOfStores > 0
  const showStoreInfoSummary = numOfStores > 0

  const initialCenter = React.useMemo(
    () => ({
      latitude: (selectedStore || {}).latitude || -1,
      longitude: (selectedStore || {}).longitude || -1
    }),
    [selectedStore]
  )

  const mapOptions = React.useMemo(() => {
    return {
      fullscreenControl: desktop,
      zoomControl: desktop,
      zoomControlOptions: {
        position: ((mapServices || {}).ControlPosition || {}).RIGHT_CENTER
      }
    }
  }, [desktop, mapServices])

  const mapMarkers = React.useMemo(() => {
    if (isObjectEmpty(mapServices) || isObjectEmpty(sortedAvailableStores)) {
      return []
    }

    return sortedAvailableStores.map((store) => {
      const marker = new mapServices.Marker({
        title: store.name,
        icon: `${origin()}${appResource('/icons/Location_Pin.svg')}`,
        position: new mapServices.LatLng(store.latitude, store.longitude)
      })

      marker.addListener('click', () => {
        setFieldValue('selectedStoreId', store.id)
        setShowInfo(true)
      })

      return marker
    })
  }, [sortedAvailableStores, mapServices, setFieldValue])

  const onDragStart = () => setShowInfo(false)

  const onToggleWrapper = () => !showInfo && setShowInfo(true)

  const onZoomChanged = () => {
    const { getZoomLevel = () => 0 } = mapUtils || {}
    if (getZoomLevel() >= 10) {
      mapMarkers.forEach((marker) => {
        marker.setLabel({
          className: 'marker-label',
          text: marker.getTitle()
        })
      })
    } else {
      mapMarkers.forEach((marker) => {
        marker.setLabel(null)
      })
    }
  }

  const onCarouselChange = (index) => {
    setFieldValue('selectedStoreId', sortedAvailableStores[index].id)
  }

  const onSetCarouselRef = (carousel) => {
    carouselRef.current = carousel
  }

  React.useEffect(() => {
    if (isObjectEmpty(selectedStore)) {
      return
    }

    if (carouselRef.current?.slickGoTo) {
      const storesIndex = sortedAvailableStores.findIndex(
        (store) => store.id === selectedStore.id
      )
      carouselRef.current.slickGoTo(storesIndex)
    }

    if (!isObjectEmpty(mapServices)) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      const { setMapCenter = () => {} } = mapUtils || {}
      setMapCenter({
        latitude: selectedStore.latitude,
        longitude: selectedStore.longitude
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore])

  return (
    <Container>
      <MapComponent
        {...mapProps}
        withMarker={false}
        withClustering={(mapMarkers || []).length > 10}
        Container={MapContainer}
        initialCenter={initialCenter}
        mapOptions={mapOptions}
        mapMarkers={mapMarkers}
        onDragStart={onDragStart}
        onZoomChanged={onZoomChanged}
      />
      <ChipContainer>
        <Chip color={colorConstants.black}>
          <SmallText normalised color={colorConstants.white}>
            {t('Drag the map to browse the area')}
          </SmallText>
        </Chip>
      </ChipContainer>
      <BottomSheetContainer>
        <Carousel
          swipe
          draggable
          slidesToShow
          innerRef={onSetCarouselRef}
          slidesToScroll={1}
          infinite={false}
          lazyLoad={false}
          dots={false}
          arrows={showArrows}
          NextArrow={NextArrow}
          PrevArrow={PrevArrow}
          afterChange={onCarouselChange}
        >
          {(sortedAvailableStores || []).map((store) => {
            return (
              <PromotionCardWrapper
                key={store.id}
                isExpanded={showInfo}
                onClick={onToggleWrapper}
              >
                <StoreInformationCard
                  selected
                  showCollectionTime
                  shouldTruncateInfo
                  showInfo={showInfo}
                  store={store}
                />
                {showStoreInfoSummary && (
                  <StoreInformationSummary
                    show={!showInfo}
                    numOfStores={numOfStores}
                    onViewStoreClick={onViewStoreClick}
                  />
                )}
              </PromotionCardWrapper>
            )
          })}
        </Carousel>
      </BottomSheetContainer>
    </Container>
  )
}

export default (props: {
  availableStores: Oms.V4.Store[]
  onViewStoreClick: () => void
}): JSX.Element => (
  <MapService mapService={mapServices.GOOGLE_MAP}>
    {(mapServiceProps) => (
      <StoreViewer mapServiceProps={mapServiceProps} {...props} />
    )}
  </MapService>
)
